import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MarketplaceItem {
  id: string;
  title: string;
  channel: string;
  creator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake";
  category: string;
  price: number;
  description: string;
  image?: string;
  isMinted: boolean;
}

export const useMarketplaceItems = (channelName?: string) => {
  return useQuery({
    queryKey: ['marketplace-items', channelName],
    queryFn: async () => {
      // Fetch minted Knowledge Bases
      let kbQuery = supabase
        .from('knowledge_bases')
        .select(`
          id,
          title,
          creator_id,
          channel_name,
          nft_cover_image_url,
          description,
          created_at,
          profiles:creator_id(display_name)
        `)
        .eq('status', 'published')
        .is('deleted_at', null);
      
      // Only filter by channel if channelName is provided
      if (channelName) {
        kbQuery = kbQuery.eq('channel_name', channelName.toUpperCase());
      }
      
      const { data: kbData, error: kbError } = await kbQuery;

      if (kbError) throw kbError;

      // Fetch minted Solutions
      let solQuery = supabase
        .from('solutions')
        .select(`
          id,
          title,
          creator_id,
          channel_name,
          nft_cover_image_url,
          base_price,
          price,
          category,
          description,
          commercial_type,
          agency,
          created_at,
          profiles:creator_id(display_name)
        `)
        .eq('status', 'published');
      
      // Only filter by channel if channelName is provided
      if (channelName) {
        solQuery = solQuery.eq('channel_name', channelName.toUpperCase());
      }
      
      const { data: solData, error: solError } = await solQuery;

      if (solError) throw solError;

      // Transform Knowledge Bases to marketplace format
      const kbItems: MarketplaceItem[] = (kbData || []).map(kb => ({
        id: kb.id,
        title: kb.title,
        channel: kb.channel_name || channelName || '',
        creator: kb.profiles?.display_name || 'Unknown',
        commercialType: 'Sale' as const,
        category: 'Knowledge Base',
        price: 0,
        description: kb.description || '',
        image: kb.nft_cover_image_url || undefined,
        isMinted: !!kb.nft_cover_image_url
      }));

      // Transform Solutions to marketplace format
      const solItems: MarketplaceItem[] = (solData || []).map(sol => ({
        id: sol.id,
        title: sol.title,
        channel: sol.channel_name || channelName || '',
        creator: sol.profiles?.display_name || 'Unknown',
        commercialType: (sol.commercial_type as "Sale" | "Lease" | "Collaborate" | "Stake") || 'Sale',
        category: sol.category || 'General',
        price: Number(sol.price) || Number(sol.base_price) || 0,
        description: sol.description || '',
        image: sol.nft_cover_image_url || undefined,
        isMinted: !!sol.nft_cover_image_url
      }));

      // Combine and sort by created_at (most recent first)
      return [...kbItems, ...solItems];
    }
  });
};
